import type { SoftphoneCarrierFailure, SoftphoneStatus } from './softphoneClient.types';

/** Twilio 13225 "Dial: Call blocked by Twilio" — an account-level refusal; every call fails until lifted. */
export const TWILIO_CALL_BLOCKED_CODE = 13225;
/** Twilio 13227 — the account's geographic permissions don't allow the destination; every call there fails. */
export const TWILIO_GEO_PERMISSION_CODE = 13227;

/**
 * Carrier errors that mean the prospect was provably never rung because of ACCOUNT configuration
 * (a Twilio hold, a country not enabled), not anything about this dial. For these the outcome
 * picker is hidden: no disposition is true, and logging "No answer" would both falsify the history
 * and advance the contact's call step as if an attempt had been made. Per-dial rejections (13224
 * invalid number) and failures with no code keep the picker — "Wrong number" is the right outcome
 * for the former, and the latter may be a far-end failure we can't characterize.
 */
export function carrierFailureBlocksOutcomes(code: number | null): boolean {
  return code === TWILIO_CALL_BLOCKED_CODE || code === TWILIO_GEO_PERMISSION_CODE;
}

export const STATUS_LABEL: Record<SoftphoneStatus, string> = {
  idle: 'Ready',
  connecting: 'Connecting…',
  ringing: 'Ringing…',
  active: 'Connected',
  ended: 'Call ended',
  error: 'Call failed',
};

export const STATUS_DOT: Record<SoftphoneStatus, string> = {
  idle: 'bg-gray-400',
  connecting: 'bg-yellow-400 animate-pulse',
  ringing: 'bg-yellow-400 animate-pulse',
  active: 'bg-green-500',
  ended: 'bg-gray-400',
  error: 'bg-red-500',
};

export function formatDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export interface PostCallView {
  /** Text for the status line. */
  label: string;
  /** Tailwind classes for the status dot. */
  dotClass: string;
  /** The call is over and the carrier reported it `failed` — render the `CallFailedNotice`. */
  carrierRefused: boolean;
  /** The failure is account-level: offer Close instead of the outcome picker. */
  blocksOutcomes: boolean;
}

/**
 * One source of truth for what the post-call screen shows, shared by every dialer surface
 * (floating, inline, extension) so they can't drift. A carrier failure only means anything once
 * the call is over — while live it's stale state from a previous call and is ignored.
 */
export function resolvePostCallView(
  status: SoftphoneStatus,
  carrierFailure: SoftphoneCarrierFailure | null | undefined,
): PostCallView {
  const finished = status === 'ended' || status === 'error';
  const carrierRefused = finished && carrierFailure != null;
  if (!carrierRefused) {
    return { label: STATUS_LABEL[status], dotClass: STATUS_DOT[status], carrierRefused: false, blocksOutcomes: false };
  }
  const blocksOutcomes = carrierFailureBlocksOutcomes(carrierFailure.code);
  return {
    // "Blocked" is reserved for account-level refusals (13225 / 13227): the carrier won't place the
    // call at all, and the rep should read it as "our account", not "this prospect". Any other code
    // is a rejection of this one dial (13224 unreachable destination, 13214 bad caller id, …), and
    // CallFailedNotice says which — "blocked" sent reps hunting for an account problem that wasn't
    // there. No code means
    // the carrier only reported the leg as failed, which for some carriers (Telnyx maps every
    // unlisted hangup cause to `failed`) may be an ordinary termination — so say "failed".
    label: blocksOutcomes ? 'Call blocked' : carrierFailure.code != null ? 'Couldn’t connect' : 'Call failed',
    dotClass: STATUS_DOT.error,
    carrierRefused: true,
    blocksOutcomes,
  };
}
